/**
 * Module dependencies.
 */

import {
  InfiniteData,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { queryKeys } from 'src/core/constants/query-keys';
import { getRoute } from 'src/core/constants/routes';
import {
  ExtendedAxiosConfig,
  axiosInstance,
  getApiEndpoint,
  getNetworkErrorTranslatedKey
} from 'src/core/utils/requests';
import { getStatusFromSurvey } from 'src/core/utils/surveys';
import { toast } from 'src/providers/toast';
import { PaginatedData } from 'src/types/paginated';
import { ApiSubmission, Submission } from 'src/types/submission';

/**
 * `InfinitePaginatedData` type
 */

type InfinitePaginatedData<TData> = InfiniteData<PaginatedData<TData>, number>;

/**
 * `cacheTime` constant.
 */

const cacheTime = 1000 * 60 * 60;

/**
 * `familiarCategory` constant.
 */

const familiarCategory = process.env.NEXT_PUBLIC_FAMILY_COMPANY_LABEL;

/**
 * Export `submittedFilter` constant.
 */

export const submittedFilter = { params: { filter: { submitted: 'true' } } };

/**
 * Export `getSubmissionCategoryScores` util.
 */

export function getSubmissionCategoryScores(submission: ApiSubmission) {
  return submission?.statistic?.reduce(
    (acc: Record<string, number>, answer) => ({
      ...acc,
      [answer?.category]: (acc?.[answer?.category] ?? 0) + Number(answer.scoreSum)
    }),
    {}
  );
}

/**
 * `normalizeSubmission` util.
 */

function normalizeSubmission(submission: ApiSubmission): Submission {
  const total = submission.statistic.reduce(
    (acc, curr) => {
      const familiarScore = curr.category === familiarCategory ? Number(curr.scoreSum) : 0;

      return {
        answers: acc.answers + curr.answeredQuestions,
        familiarPillarScore: acc.familiarPillarScore + familiarScore,
        questions: acc.questions + curr.requiredQuestions,
        score: acc.score + Number(curr.scoreSum)
      };
    },
    { answers: 0, familiarPillarScore: 0, questions: 0, score: 0 }
  );

  return {
    ...submission,
    familiarPillarScore: total.familiarPillarScore,
    score: total.score,
    scoreByCategory: getSubmissionCategoryScores(submission),
    status: getStatusFromSurvey(submission),
    totalAnswers: total.answers,
    totalQuestions: total.questions
  };
}

/**
 * Export `createSubmission` request.
 */

export async function createSubmission(): Promise<Submission> {
  const response = await axiosInstance.post(getApiEndpoint('submissions'), undefined);

  return normalizeSubmission(response.data) as Submission;
}

/**
 * Export `useCreateSubmission` hook.
 */

export function useCreateSubmission(options?: UseMutationOptions<Submission, AxiosError, void>) {
  const { t } = useTranslation();
  const router = useRouter();
  const queryClient = useQueryClient();

  return useMutation<Submission, AxiosError, void>({
    mutationFn: createSubmission,
    ...options,
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context);
      const errorMessage = getNetworkErrorTranslatedKey({
        basePath: 'common:errors.createSurvey.',
        error
      });

      toast.error(t(errorMessage, { returnObjects: true }));
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);
      queryClient.invalidateQueries({ queryKey: queryKeys.submission.infinite });
      router.push(getRoute('survey.ongoing', { id: data.id }));
    }
  });
}

/**
 * Export `submitSubmission` request.
 */

export async function submitSubmission(id: string, options?: ExtendedAxiosConfig): Promise<Submission> {
  const response = await axiosInstance.patch(getApiEndpoint('submitSubmission', { id }), undefined, options);

  return normalizeSubmission(response.data) as Submission;
}

/**
 * Export `deleteSubmission` request.
 */

export async function deleteSubmission(id: string, options?: ExtendedAxiosConfig): Promise<void> {
  const response = await axiosInstance.delete(getApiEndpoint('submissionById', { id }), options);

  return response?.data;
}

/**
 * Export `duplicateSubmission` request.
 */

export async function duplicateSubmission(id: string, options?: ExtendedAxiosConfig): Promise<Submission> {
  const response = await axiosInstance.post(getApiEndpoint('duplicateSubmission', { id }), options);

  return normalizeSubmission(response.data) as Submission;
}

/**
 * Export `getSubmissionById` request.
 */

export async function getSubmissionById(id: string, options?: ExtendedAxiosConfig): Promise<Submission> {
  const response = await axiosInstance.get(getApiEndpoint('submissionById', { id }), options);

  return normalizeSubmission(response.data);
}

/**
 * Export `getPreviousSubmissionById` request.
 */

export async function getPreviousSubmissionById(
  id: string,
  options?: ExtendedAxiosConfig
): Promise<Submission | undefined> {
  const response = await axiosInstance.get(getApiEndpoint('previousSubmissionById', { id }), options);

  return normalizeSubmission(response.data);
}

/**
 * Export `getSubmissions` request.
 */

export async function getSubmissions(options?: ExtendedAxiosConfig): Promise<PaginatedData<Submission>> {
  const response = await axiosInstance.get(getApiEndpoint('submissions'), options);

  return {
    ...response.data,
    data: response.data.data.map(normalizeSubmission)
  };
}

/**
 * Export `useSubmission` request.
 */

export function useSubmission(id: string, options?: Omit<UseQueryOptions<Submission>, 'queryFn' | 'queryKey'>) {
  return useQuery({
    gcTime: cacheTime,
    queryFn: () => getSubmissionById(id),
    queryKey: queryKeys.submission.byId(id),
    ...options
  });
}

/**
 * Export `usePreviousSubmission` request.
 */

export function usePreviousSubmission(
  id: string,
  options?: Omit<UseQueryOptions<Submission | undefined>, 'queryFn' | 'queryKey'>
) {
  return useQuery({
    enabled: !!id,
    gcTime: cacheTime,
    queryFn: () => getPreviousSubmissionById(id),
    queryKey: queryKeys.submission.previous(id),
    ...options
  });
}

/**
 * Export `useSubmittedSubmissions` request.
 */

export function useSubmittedSubmissions(
  options?: Omit<UseQueryOptions<PaginatedData<Submission>>, 'queryFn' | 'queryKey'>
) {
  return useQuery({
    gcTime: cacheTime,
    queryFn: () => getSubmissions(submittedFilter as any),
    queryKey: queryKeys.submission.submittedList,
    ...options
  });
}

/**
 * Export `useSubmissions` request.
 */

export function useSubmissions(options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    enabled: options?.enabled,
    gcTime: cacheTime,
    getNextPageParam: (page: PaginatedData<Submission>) => {
      const nextPage = page.currentPage + 1;

      return nextPage <= page.lastPage ? nextPage : undefined;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam }) => getSubmissions({ params: { page: pageParam } } as any),
    queryKey: queryKeys.submission.infinite
  });
}

/**
 * `useSubmitSubmission` hook.
 */

export function useSubmitSubmission(options?: UseMutationOptions<Submission, AxiosError, string>) {
  const { t } = useTranslation(['common', 'survey']);

  return useMutation<Submission, AxiosError, string>({
    ...options,
    mutationFn: id => submitSubmission(id),
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context);

      if (error.response?.status !== 422) {
        const errorMessage = getNetworkErrorTranslatedKey({
          basePath: 'survey:errors.form.',
          error
        });

        toast.error(t(errorMessage, { returnObjects: true }));
      }
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context);

      toast.success(
        t('survey:detail.submitSuccess', {
          id: data.submissionNumber,
          returnObjects: true
        })
      );
    }
  });
}

/**
 * `useDeleteSubmission` hook.
 */

export function useDeleteSubmission(options?: UseMutationOptions<void, AxiosError, string>) {
  const { t } = useTranslation(['common', 'survey']);
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, string>({
    ...options,
    mutationFn: id => deleteSubmission(id),
    onError: (error: AxiosError) => {
      const errorMessage = getNetworkErrorTranslatedKey({
        basePath: 'survey:errors.form.',
        error
      });

      toast.error(t(errorMessage, { returnObjects: true }));
    },
    onSuccess: (data, id, onMutateResult, context) => {
      const queryKey = queryKeys.submission.infinite;

      queryClient.invalidateQueries({ queryKey });
      queryClient.setQueryData(queryKey, (oldData?: InfinitePaginatedData<Submission>) => {
        if (!oldData?.pages?.length) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map(page => ({
            ...page,
            data: page.data.filter(item => item.id !== id)
          }))
        };
      });

      toast.success(t('dashboard:survey.deleteSuccess', { returnObjects: true }));

      if (options?.onSuccess) {
        options.onSuccess(data, id, onMutateResult, context);
      }
    }
  });
}

/**
 * `useDuplicateSubmission` hook.
 */

export function useDuplicateSubmission(options?: UseMutationOptions<Submission, AxiosError, string>) {
  const { t } = useTranslation(['common', 'survey']);
  const queryClient = useQueryClient();

  return useMutation<Submission, AxiosError, string>({
    ...options,
    mutationFn: id => duplicateSubmission(id),
    onError: (error: AxiosError) => {
      const errorMessage = getNetworkErrorTranslatedKey({
        basePath: 'survey:errors.form.',
        error
      });

      toast.error(t(errorMessage, { returnObjects: true }));
    },
    onSuccess: (newSurvey, variables, onMutateResult, context) => {
      const queryKey = queryKeys.submission.infinite;

      queryClient.invalidateQueries({ queryKey });
      queryClient.setQueryData(queryKey, (oldData?: InfinitePaginatedData<Submission>) => {
        if (!oldData?.pages?.length) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page, index) => ({
            ...page,
            data: index === 0 ? [newSurvey, ...page.data] : page.data
          }))
        };
      });

      toast.success(
        t('dashboard:survey.duplicateSuccess', {
          id: newSurvey.submissionNumber,
          returnObjects: true
        })
      );

      if (options?.onSuccess) {
        options.onSuccess(newSurvey, variables, onMutateResult, context);
      }
    }
  });
}
